/**
 * The tender data the pane shows: the bid record (summary, criteria, dates)
 * and its outline, plus the organisation's document templates. Writes to
 * questions go through the /word routes (questionsApi.ts).
 */
import type { TemplateStyleMap } from "../document/identity";
import axios from "../helper/axios";
import { apiURL } from "../helper/urls";

export type TenderSection = {
  section_id: string;
  section_number?: string;
  heading: string;
  question?: string;
  word_count?: number | null;
  limit_value?: number | null;
  limit_type?: "words" | "pages" | "characters" | null;
  weighting?: string;
  writingplan?: string;
  relevant_evaluation_criteria?: string;
  storyboard_fields?: { win_themes?: string; solution?: string; pain_points?: string; differentiators?: string };
  /** Everything else the server stores; sent back untouched on save. */
  [key: string]: unknown;
};

export type TenderDetails = {
  _id: string;
  bid_title: string;
  client_name?: string;
  value?: string;
  submission_deadline?: string;
  tender_summary?: string;
  evaluation_criteria?: string;
  win_themes?: string;
  outline: TenderSection[];
  timestamp?: string;
};

export type DocumentTemplate = { file_id: string; filename: string; uploaded_by?: string; original_format?: string };

const auth = (token: string) => ({ Authorization: `Bearer ${token}` });

/** Mongo's extended JSON wraps ids as `{ $oid }`; the pane wants strings. */
const idOf = (raw: unknown): string =>
  typeof raw === "string" ? raw : ((raw as { $oid?: string } | null)?.$oid ?? String(raw));

export const fetchTender = async (bidId: string, token: string, signal?: AbortSignal): Promise<TenderDetails> => {
  const { data } = await axios.get(apiURL(`get_bid/${bidId}`), { headers: auth(token), signal });
  const bid = data?.bid ?? {};
  return {
    ...bid,
    _id: idOf(bid._id ?? bidId),
    bid_title: bid.bid_title ?? "Untitled tender",
    outline: Array.isArray(bid.outline) ? bid.outline : [],
  };
};

export const fetchTemplates = async (token: string, signal?: AbortSignal): Promise<DocumentTemplate[]> => {
  const { data } = await axios.get(apiURL("get_proposal_templates"), { headers: auth(token), signal });
  const list: DocumentTemplate[] = data?.status === "success" ? (data.proposal_templates ?? []) : [];
  return list.filter((t) => (t.original_format ?? "docx") === "docx");
};

export const uploadTemplate = async (file: File, token: string): Promise<void> => {
  const form = new FormData();
  form.append("file", file);
  form.append("mode", "word");
  await axios.post(apiURL("uploadfile_proposal_template"), form, { headers: auth(token) });
};

/** Which styles the template's own body pages use (see the server's body_style_map). */
export const fetchTemplateStyleMap = async (fileId: string, token: string): Promise<TemplateStyleMap | null> => {
  const { data } = await axios.get(apiURL(`proposal_template/${fileId}/style_map`), { headers: auth(token) });
  const map = data?.style_map;
  return map && typeof map === "object" && map.body?.id ? (map as TemplateStyleMap) : null;
};

/** The template's .docx as base64, the form `Word.Application.createDocument` takes. */
export const downloadTemplateBase64 = async (fileId: string, token: string): Promise<string> => {
  const { data } = await axios.get<ArrayBuffer>(apiURL(`proposal_template/${fileId}/download`), {
    headers: auth(token),
    responseType: "arraybuffer",
  });
  const bytes = new Uint8Array(data);
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
};
